import { changeTwoStepPaymentFormStepType } from 'client/actions/actionsManagement'
import { useUpdateProp } from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { PaypalBackgroundColor } from 'client/pages/offer/settings/paypal-button-settings'
import { isPaypalSettingsOpened } from 'client/reducers/managementReducer'
import {
  managementSelectors,
  pageSelectors,
  useManagement,
  usePage,
  usePayment,
} from 'client/store'
import { togglePayPalSettings } from 'client/store/management/managementActions'
import { isPaymentMethodAvailable } from 'client/store/payment/paymentSelectors'
import { PaymentFormStepTypeEnum } from 'common/enums/PaymentFormStepTypeEnum'
import { PaymentMethodEnum } from 'common/enums/PaymentMethodEnum'
import { useActions } from 'common/hooks/useActions'
import React from 'react'
import { useDispatch } from 'react-redux'
import { Switcher } from 'src/editor/shared/ui-lib/switcher'
import { Tooltip } from 'src/shared/components/tooltip'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { EntityIdType } from 'src/shared/schemas/entity-id.schema'
import Input from '../../components/Input/Input'
import NewCheckbox from '../../components/NewCheckbox'
import GroupTitle from '../../styleComponents/GroupTitle'

export interface EditStepSwitcherProps {
  optInStepEntityId?: EntityIdType
  paymentStepEntityId?: EntityIdType
}

const EditStepSwitcher = ({
  optInStepEntityId,
  paymentStepEntityId,
}: EditStepSwitcherProps) => {
  const stepType = useManagement(
    managementSelectors.getTwoStepPaymentFormStepType,
  )
  const changePreviewStep = useActions(changeTwoStepPaymentFormStepType)
  const stepOptInEntity = usePage(page =>
    pageSelectors.getEntityById(page, optInStepEntityId),
  )
  const stepPaymentEntity = usePage(page =>
    pageSelectors.getEntityById(page, paymentStepEntityId),
  )
  const dispatch = useDispatch()
  const { t } = useTypedTranslation()
  const updateOptInStepProp = useUpdateProp(stepOptInEntity)
  const updatePaymentStepProp = useUpdateProp(stepPaymentEntity)
  const isStepOptIn = stepType === PaymentFormStepTypeEnum.STEP_OPT_IN
  const isStepPayment = stepType === PaymentFormStepTypeEnum.STEP_PAYMENT
  const isPaypalAvailable = usePayment(state =>
    isPaymentMethodAvailable(state, PaymentMethodEnum.PaypalRest),
  )
  const previewPaypalSettings = useManagement(isPaypalSettingsOpened)

  const updatePaypalBackgroundColor = (color: string) => {
    if (!stepPaymentEntity.paypal) stepPaymentEntity.paypal = {}

    const updatedPaypalProps = {
      ...stepPaymentEntity.paypal,
      backgroundColor: color,
    }
    updatePaymentStepProp('paypal')(updatedPaypalProps)
  }

  return (
    <React.Fragment>
      <Switcher
        btnList={[
          {
            value: PaymentFormStepTypeEnum.STEP_OPT_IN,
            content: 'entity_settings.two_step_payment_form.switch_opt_in_step',
          },
          {
            value: PaymentFormStepTypeEnum.STEP_PAYMENT,
            content:
              'entity_settings.two_step_payment_form.switch_payment_step',
          },
        ]}
        value={stepType}
        update={changePreviewStep}
      />

      {isStepOptIn && (
        <React.Fragment>
          <Input
            value={stepOptInEntity.headline}
            update={updateOptInStepProp('headline')}
            label="entity_settings.two_step_payment_form.headline"
          />
          <Input
            value={stepOptInEntity.subHeadline}
            update={updateOptInStepProp('subHeadline')}
            label="entity_settings.two_step_payment_form.sub_headline"
          />
          <Input
            value={stepOptInEntity.buttonText}
            update={updateOptInStepProp('buttonText')}
            label="entity_settings.two_step_payment_form.step_button_text"
          />
          <Input
            value={stepOptInEntity.buttonSubText}
            update={updateOptInStepProp('buttonSubText')}
            label="entity_settings.two_step_payment_form.step_button_sub_text"
          />
          <Input
            value={stepOptInEntity.footerText}
            update={updateOptInStepProp('footerText')}
            label="entity_settings.two_step_payment_form.footer_text"
          />
        </React.Fragment>
      )}
      {isStepPayment && (
        <React.Fragment>
          {isPaypalAvailable && (
            <>
              <GroupTitle
                style={{ display: 'flex', alignItems: 'center' }}
                afterChildren={
                  <Tooltip
                    message={t(
                      'entity_settings.payment_paypal.limitation_tooltip',
                    )}
                  />
                }
              >
                entity_settings.payment_paypal.group_title
              </GroupTitle>
              <NewCheckbox
                labelText="entity_settings.paypal.preview_button"
                helpMessage={
                  'entity_settings.payment_paypal.preview_button_tooltip'
                }
                checked={previewPaypalSettings}
                update={value => dispatch(togglePayPalSettings(value))}
              />
              <PaypalBackgroundColor
                color={stepPaymentEntity?.paypal?.backgroundColor}
                update={updatePaypalBackgroundColor}
              />
              <GroupTitle>
                entity_settings.payment_common.group_title
              </GroupTitle>
            </>
          )}
          <Input
            value={stepPaymentEntity.headline}
            update={updatePaymentStepProp('headline')}
            label="entity_settings.two_step_payment_form.headline"
          />
          <Input
            value={stepPaymentEntity.subHeadline}
            update={updatePaymentStepProp('subHeadline')}
            label="entity_settings.two_step_payment_form.sub_headline"
          />
          <Input
            value={stepPaymentEntity.buttonText}
            update={updatePaymentStepProp('buttonText')}
            label="entity_settings.two_step_payment_form.step_button_text"
          />
          <Input
            value={stepPaymentEntity.buttonSubText}
            update={updatePaymentStepProp('buttonSubText')}
            label="entity_settings.two_step_payment_form.step_button_sub_text"
          />
          <Input
            value={stepPaymentEntity.footerText}
            update={updatePaymentStepProp('footerText')}
            label="entity_settings.two_step_payment_form.footer_text"
          />
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

export default EditStepSwitcher
